// OAuth 成功回调页 —— console-api 把用户重定向回 #/oauth/success?token=<jwt>
// 解码 JWT 拿出 sub/email，写到 storage 并跳转到 /sites。

import { useEffect, useState } from 'react';
import { writeSession } from '../lib/storage';
import { navigate, parseHashQuery } from '../lib/route';

interface JwtPayload {
  sub: string;
  tid: string;
  role: string;
  iat: number;
  exp: number;
  sid: string;
  /** 服务端没把 email 放 JWT；登录后调 /me 拿（MVP 暂用 sub 兜底） */
}

function decodeJwtPayload(jwt: string): JwtPayload | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    let b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const json = atob(b64);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

export function OAuthSuccess(): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = parseHashQuery();
    const token = q.get('token');
    const oauthError = q.get('oauth_error');

    if (oauthError) {
      setError(`OAuth 失败：${oauthError}`);
      return;
    }
    if (!token) {
      setError('缺少 token 参数');
      return;
    }

    const payload = decodeJwtPayload(token);
    if (payload === null) {
      setError('JWT 解析失败');
      return;
    }

    writeSession({
      token,
      email: payload.sub, // OAuth 用户回调时只能拿到 user_id；之后 SPA 调 /me 完善
      role: payload.role,
    });

    // 立即跳转
    setTimeout(() => navigate('sites'), 200);
  }, []);

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {error === null ? (
          <>
            <div style={spinnerStyle} />
            <h2 style={titleStyle}>正在登录…</h2>
            <p style={subtitleStyle}>处理 OAuth 凭据中</p>
          </>
        ) : (
          <>
            <div style={errorIconStyle}>⚠️</div>
            <h2 style={titleStyle}>登录失败</h2>
            <p style={subtitleStyle}>{error}</p>
            <a
              href="#/login"
              style={btnStyle}
              onClick={(e) => {
                e.preventDefault();
                navigate('login');
              }}
            >
              返回登录
            </a>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
};
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 360,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: 32,
  textAlign: 'center',
  boxShadow: '0 24px 50px -28px rgba(15,23,42,.18)',
};
const titleStyle: React.CSSProperties = { fontSize: 18, margin: '16px 0 4px', color: '#0f172a' };
const subtitleStyle: React.CSSProperties = { fontSize: 13, color: '#64748b', margin: 0 };
const spinnerStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  border: '3px solid #e2e8f0',
  borderTopColor: '#2563eb',
  borderRadius: '50%',
  margin: '0 auto',
  animation: 'spin 0.9s linear infinite',
};
const errorIconStyle: React.CSSProperties = { fontSize: 36 };
const btnStyle: React.CSSProperties = {
  display: 'inline-block',
  marginTop: 18,
  padding: '8px 18px',
  background: '#2563eb',
  color: '#fff',
  borderRadius: 8,
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
};
